<template>
    <div class="modal" id="shift_details_modal" tabindex="-1" aria-labelledby="shift_details_modal" aria-hidden="true">
        <div class="modal-dialog">
            <div class="modal-content shadow-3" v-if="shift === null">
                <div class="modal-body text-center">
                    <div class="spinner-border spinner-border-sm" role="status">
                        <span class="visually-hidden">{{ $trans.get('__JSON__.Loading...') }}</span>
                    </div>
                </div>
            </div>
            <div class="modal-content shadow-3" v-else>
                <div class="modal-header flex-wrap pb-0" :class="{ 'pb-5': editing }">
                    <h5 class="modal-title">
                        <small class="text-muted">{{ shiftTitle }}</small>
                        <span class="d-block">{{ shift.details.job }}</span>
                    </h5>
                    <div class="ms-auto">
                        <div class="d-flex mx-n2">
                            <div class="btn-group btn-group-sm">
                                <button class="btn btn-sm btn-neutral" @click="switchMode('mutate')" v-can="{ ability: 'shift.extra', args: {'Knikkers\\Shifts\\Shift': shiftId} }" v-if="! editing">
                                    <span>{{ $trans.get('__JSON__.Quantity') }}</span>
                                </button>
                                <button class="btn btn-sm btn-neutral" @click="switchMode('edit')" v-can="{ ability: 'shift.edit', args: {'Knikkers\\Shifts\\Shift': shiftId} }" v-if="canEdit">
                                    {{ $trans.get('__JSON__.Specs') }}
                                </button>
                                <button class="btn btn-sm btn-outline-danger" @click="removeConfirm" v-can="{ ability: 'shift.delete', args: {'Knikkers\\Shifts\\Shift': shiftId} }" v-if="! hasEnrollments && ! editing">
                                    {{ $trans.get('__JSON__.Remove') }}
                                </button>
                            </div>
                        </div>
                    </div>
                    <span class="d-block w-full"></span>
                    <ul class="nav nav-sm nav-tabs border-0" v-if="! editing">
                        <li class="nav-item" role="presentation">
                            <button class="nav-link py-4 active" data-bs-toggle="tab" data-bs-target="#details" role="tab">
                                {{ $trans.get('__JSON__.Details') }}
                            </button>
                        </li>
                        <li class="nav-item" role="presentation" v-can="'shift.view.enrollments'">
                            <button class="nav-link py-4 font-regular" data-bs-toggle="tab" data-bs-target="#enrollments" role="tab">
                                {{ $trans.get('__JSON__.Enrollments') }}
                                <span class="badge bg-light rounded-circle text-black" v-if="hasEnrollments">
                                    {{ shift.enrollments.length }}
                                </span>
                            </button>
                        </li>
                        <li class="nav-item" role="presentation" v-can="'shift.view.mutations'" v-if="hasMutations">
                            <button class="nav-link py-4 font-regular" data-bs-toggle="tab" data-bs-target="#mutations" role="tab">
                                {{ $trans.get('__JSON__.Mutations') }}
                                <span class="badge bg-tint-danger rounded-circle">
                                    {{ shift.mutations.length }}
                                </span>
                            </button>
                        </li>
                    </ul>
                </div>

                <div class="d-block w-full" v-if="editing && shift.recurring">
                    <div class="alert alert-info" style="font-size: 12px; padding: 5px 20px; border-radius: 0; border-top: 0">
                        {{ $trans.get('__JSON__.This shift is part of a recurring shift (:from until :until)', { from: formatDate(shift.recurring_range.start, DATE_YMD), until: formatDate(shift.recurring_range.end, DATE_YMD) }) }}
                    </div>
                </div>

                <div class="modal-body py-0">
                    <AddOrRevoke :department="department" :shift="shift" :user="user" v-if="isAdminOrManager && mode === 'mutate'" ref="mutate" />
                    <EditShift :department="department" :shift="shift" :user="user" v-else-if="isAdminOrManager && mode === 'edit'" ref="edit" />

                    <div class="tab-content" v-else-if="mode === 'details'">
                        <div class="tab-pane show active pt-5 pb-5" id="details" role="tabpanel">
                            <div class="list-group mb-5">
                                <div class="list-group-item">
                                    <div class="d-flex justify-content-between">
                                        <h6 class="text-muted mb-0 d-block">
                                            {{ $trans.get('__JSON__.Client location') }}
                                        </h6>
                                    </div>
                                    <p class="progress-text">
                                        {{ department.name }}
                                    </p>
                                </div>
                                <div class="list-group-item">
                                    <div class="d-flex justify-content-between">
                                        <h6 class="text-muted mb-0 d-block">
                                            {{ $trans.get('__JSON__.Job') }}
                                        </h6>
                                    </div>
                                    <p class="progress-text">
                                        {{ shift.details.job }}
                                    </p>
                                </div>
                                <div class="list-group-item">
                                    <div class="d-flex justify-content-between">
                                        <h6 class="text-muted mb-0 d-block">
                                            {{ $trans.get('__JSON__.Start') }}
                                        </h6>
                                    </div>
                                    <p class="progress-text">
                                        {{ formatDate(shift.details.start) }}
                                    </p>
                                </div>
                                <div class="list-group-item">
                                    <div class="d-flex justify-content-between">
                                        <h6 class="text-muted mb-0 d-block">
                                            {{ $trans.get('__JSON__.Ends') }}
                                        </h6>
                                    </div>
                                    <p class="progress-text">
                                        {{ formatDate(shift.details.end) }}
                                    </p>
                                </div>
                                <div class="list-group-item d-none">
                                    <div class="d-flex justify-content-between">
                                        <h6 class="text-muted mb-0 d-block">
                                            {{ $trans.get('__JSON__.Duration') }}
                                        </h6>
                                    </div>
                                    <p class="progress-text">
                                        {{ $trans.get('__JSON__.:amount hours', { amount: shift.details.workingHours }) }}
                                    </p>
                                </div>
                                <div class="list-group-item" v-if="shiftNote">
                                    <div class="d-flex justify-content-between">
                                        <h6 class="text-muted mb-0 d-block">
                                            {{ $trans.get('__JSON__.Note') }}
                                        </h6>
                                    </div>
                                    <p class="progress-text" v-html="shiftNote" />
                                </div>
                            </div>
                        </div>

                        <div class="tab-pane" id="enrollments" role="tabpanel" v-can="'shift.view.enrollments'">
                            <div class="list-group list-group-flush">
                                <div class="mt-5 mb-5 text-center text-muted font-italic" v-if="! hasEnrollments">
                                    <small>{{ $trans.get('__JSON__.No enrollments found') }}</small>
                                </div>
                                <div class="list-group-item d-flex align-items-center px-0" v-for="enrollment in shift.enrollments" v-else>
                                    <div>
                                        <div class="avatar rounded-circle">
                                            <img :alt="enrollment.employee" :src="enrollment.avatar">
                                        </div>
                                    </div>
                                    <div class="d-flex flex-fill ms-3 align-items-center">
                                        <div class="text-sm text-wrap">
                                            <span class="font-semibold text-heading">{{ enrollment.employee }}</span>
                                        </div>
                                        <div class="ms-auto text-end">
                                            <div class="btn-group">
                                                <button class="btn btn-sm btn-square btn-outline-danger rounded-circle" @click="removeEnroll(enrollment)" v-can="{ ability: 'shift.enroll.remove', args: {'Knikkers\\Shifts\\Enrollment': enrollment.id} }" :title="$trans.get('__JSON__.Remove Enrollment')" tooltip>
                                                    <i class="bi bi-person-x-fill"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="shadow-none border-top mb-5" v-can="{ ability: 'admin.enroll', args: {'Knikkers\\Shifts\\Shift': shiftId} }" v-if="isAdmin && canEnroll">
                                <div class="input-group input-group-inline mt-5">
                                    <span class="input-group-text bg-transparent border-0 shadow-none ps-0 pe-3">
                                        <i class="bi bi-person"></i>
                                    </span>
                                    <vue-multiselect
                                        :options="employees"
                                        :disabled="enrolling"
                                        :placeholder="$trans.get('__JSON__.Enroll a Employee')"
                                        :selectLabel="$trans.get('__JSON__.Hit enter to select')"
                                        :selectedLabel="$trans.get('__JSON__.Selected')"
                                        :deselectLabel="$trans.get('__JSON__.Hit enter to deselect')"
                                        label="name"
                                        track-by="id"
                                        class="form-select form-select-sm"
                                        v-model="enrollItem"
                                    />
                                    <button class="btn btn-sm btn-primary" :disabled="! enrollItem || enrolling" @click="enrollEmployee">
                                        {{ $trans.get('__JSON__.Enroll') }}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div class="tab-pane pt-5 pb-5" id="mutations" role="tabpanel" v-can="'shift.view.mutations'">
                            <div class="text-center text-muted font-italic" v-if="! hasMutations">
                                <small>{{ $trans.get('__JSON__.No mutations found') }}</small>
                            </div>
                            <table class="table" v-else>
                                <thead>
                                <tr>
                                    <th>{{ $trans.get('__JSON__.Who') }}</th>
                                    <th>{{ $trans.get('__JSON__.Minutes') }}</th>
                                    <th>{{ $trans.get('__JSON__.Reason') }}</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="record in shift.mutations">
                                    <td>
                                        <strong>{{ record.who }}</strong>
                                        <span class="d-block text-small">{{ record.when }}</span>
                                    </td>
                                    <td>{{ $trans.get('__JSON__.:amount minutes', { amount: record.mutationHuman}) }}
                                    </td>
                                    <td>
                                        <p class="text-wrap">{{ record.reason }}</p>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="modal-footer" v-if="! isEmployee && editing">
                    <button type="button" class="btn btn-sm btn-neutral" @click="editing = false">
                        {{ $trans.get('__JSON__.Cancel') }}
                    </button>
                    <button type="button" class="btn btn-sm btn-primary" v-if="editing" @click="$refs[mode].save()" :disabled="disableSave">
                        {{ $trans.get('__JSON__.Save') }}
                    </button>
                </div>
                <div class="modal-footer" v-else>
                    <div class="me-auto text-muted">
                        <p class="text-sm" v-if="isEmployee">
                            {{ $trans.get('__JSON__.:amount shifts remaining', { amount: shift.details.slots.left }) }}
                        </p>
                        <p class="text-sm" v-else>
                            {{ $trans.get('__JSON__.:amount/:total shifts filled', { amount: shift.details.slots.taken, total: shift.details.slots.total }) }}
                            <small class="d-block">{{ $trans.get('__JSON__.Latest shift added at :datetime', { datetime: formatDate(shift.latest_shift_added_at, DATETIME) }) }}</small>
                        </p>
                    </div>
                    <button type="button" class="btn btn-sm btn-neutral" data-bs-dismiss="modal">
                        {{ $trans.get('__JSON__.Close') }}
                    </button>
                    <button type="button" class="btn btn-sm btn-danger" disabled v-if="isFull(shift) && ! isEnrolled(shift) && isEmployee" :title="$trans.get('__JSON__.This shift is full, sorry!')" tooltip>
                        {{ $trans.get('__JSON__.Full') }}
                    </button>
                    <button type="button" class="btn btn-sm btn-success" disabled v-if="isEnrolled(shift) && isEmployee" :title="$trans.get('__JSON__.You are enrolled for this shift')" tooltip>
                        {{ $trans.get('__JSON__.Enrolled') }}
                    </button>
                    <button type="button" class="btn btn-sm btn-primary" v-can="{ ability: 'shift.enroll', args: {'Knikkers\\Shifts\\Shift': shiftId} }" @click="enroll" v-if="isEmployee && ! isEnrolled(shift)">
                        {{ $trans.get('__JSON__.Enroll') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DateHelpers from './../mixins/DateHelpers'
import ModalHelper from './../mixins/ModalHelper'
import RequestHandler from './../mixins/RequestHandler'
import ScheduleHelpers from './../mixins/ScheduleHelpers'
import EditShift from './EditShiftComponent.vue'
import AddOrRevoke from './AddOrRevokeComponent.vue'

export default {
    name: 'ShiftDetails',
    props: {
        shiftId: {
            type: String,
            required: true,
        },
        user: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            mode: 'details', // details, edit or mutate
            editing: false,
            fetching: false,
            isListening: false,
            disableSave: true,
            changingSlots: false,

            shift: null,
            department: null,

            enrollItem: null,
            enrolling: false,
            employees: [],
            tooltips: [],
        }
    },
    watch: {
        mode(mode) {
            this.editing = (mode !== 'details')
        },
        editing() {
            if (! this.editing) this.mode = 'details'
        },
        department() {
            if (this.department.id) this.listenForEvents()
        },
    },
    mixins: [DateHelpers, ModalHelper, RequestHandler, ScheduleHelpers],
    components: {AddOrRevoke, EditShift},
    computed: {
        canEdit() {
            if (this.hasEnrollments && this.isAdmin && ! this.editing) return true
            else if (this.hasEnrollments && this.isManager && ! this.editing) return false
            else return ! this.editing
        },
        canEnroll() {
            if (this.shift.details.status !== 'open') return false
            else if (this.shift.details.slots.left <= 0) return false
            else return this.isAdmin
        },
        shiftTitle() {
            const status = this.shift.details.status

            if (this.editing && this.mode === 'edit') return this.$trans.get('__JSON__.Edit Shift')
            else if (this.editing && this.mode === 'mutate') {
                if (this.isAdmin) return this.$trans.get('__JSON__.Change Quantity')
                else return this.$trans.get('__JSON__.Add extra shifts')
            }

            else if (status === 'past-enrolled') return this.$trans.get('__JSON__.Past Enrolled Shift')
            else if (status === 'past') return this.$trans.get('__JSON__.Past Shift')
            else if (status === 'full') return this.$trans.get('__JSON__.Full Shift')
            else if (status === 'enrolled') return this.$trans.get('__JSON__.Enrolled Shift')
            else if (status === 'open') return this.$trans.get('__JSON__.Open Shift')
            else return this.$trans.get('__JSON__.Shift')
        },
        hasEnrollments() {
            const items = this.shift.enrollments
            if (! items) return false
            return items.length > 0
        },
        hasMutations() {
            const items = this.shift.mutations
            if (! items) return false
            return items.length > 0
        },
        shiftNote() {
            const note = this.shift.details.note

            if (this.shift.recurring && this.shift.parent_id !== this.shiftId) {
                const parentNote = this.shift.parent_note

                if (parentNote && note) return `${parentNote} <hr/> <h6 class="text-muted mb-0 d-block">${this.$trans.get('__JSON__.Addition')}:</h6> ${note}`
                else if (parentNote && ! note) return parentNote
            }

            return note
        },
    },
    methods: {
        switchMode(mode) {
            this.tooltips.filter(t => t._hoverState = 'show').forEach(t => t.hide())
            this.mode = mode
        },
        stopEditing() {
            this.editing = false
        },
        enroll() {
            this.makeRequest('post', '/api/shifts/enroll', { shift: this.shiftId })
        },
        async fetchDetails() {
            if (this.fetching) return

            this.fetching = true

            this.makeRequest('get', `/api/shifts/${this.shiftId}`, null, response => {
                const data = response.data
                this.shift = data.shift
                this.department = data.location
                this.employees = data.employees
            }, error => console.error(error), () => this.fetching = false)
        },
        listenForEvents() {
            if (this.isListening) return
            const locationId = this.department.id

            window.Echo.private(`shifts.${locationId}`)
                .listen('.ShiftUpdated', async event => {
                    if (event.shift.id !== this.shift.id) return
                    await this.fetchDetails()
                })
                .listen('.RefreshAll', async event => {
                    if (event?.shiftDeleted) return
                    await this.fetchDetails()
                })

            this.isListening = true
        },
        enrollEmployee() {
            if (! this.isAdmin) return
            this.enrolling = true
            if (! this.enrollItem) return

            this.makeRequest('post', `/api/shifts/${this.shiftId}/employees/enroll`, {
                shift: this.shiftId,
                employee: this.enrollItem.id,
            }, response => {
                this.enrollItem = null
            }, error => {
                // empty on purpose
            }, () => this.enrolling = false)
        },
        removeEnroll(enrollment) {
            this.confirm(this.$trans.get('__JSON__.Are you sure?'), () => {
                this.makeRequest('delete', `/api/enrollments/${enrollment.id}`)
            })
        },
        removeConfirm() {
            if (this.shift.has_future_shifts) {
                let isCurrentParent = this.shiftId === this.shift.parent_id

                if (isCurrentParent) {
                    this.confirmModal('shift_details_modal', [
                        {label: this.$trans.get('__JSON__.Delete parent shift'), variant: 'danger', onClick: async () => {
                            this.delete(this.shiftId)
                            await this.closeConfirm('shift_details_modal')
                        }},
                    ], null, this.$trans.get('__JSON__.Deleting the parent will result in decoupled children shifts'))

                    return
                }

                this.confirmModal('shift_details_modal', [
                    {label: this.$trans.get('__JSON__.Delete all future shifts'), variant: 'outline-danger', onClick: async () => {
                        this.delete(this.shift.parent_id, this.shiftId)
                        await this.closeConfirm('shift_details_modal')
                    }},
                    {label: this.$trans.get('__JSON__.Delete this shift only'), variant: 'danger', onClick: async () => {
                        this.delete(this.shiftId)
                        await this.closeConfirm('shift_details_modal')
                    }},
                ])

                return
            }

            this.confirmModal('shift_details_modal',[
                {label: this.$trans.get('__JSON__.Delete'), variant: 'danger', onClick: () => this.delete(this.shiftId)}
            ])
        },
        delete(shiftId, pointer = null) {
            this.makeRequest('delete', `/api/shifts/${shiftId}`, { pointer }, response => {
                this.closeConfirm('shift_details_modal')
            })
        },
    },
    async mounted() {
        await this.fetchDetails()
        this.tooltips = [].slice.call(document.querySelectorAll('[tooltip]'))
            .map(element => new bootstrap.Tooltip(element));
    },
}
</script>
